import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Edit, Trash2, Car } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../context/AdminContext';
import ImageUpload from '../../components/ImageUpload';

const getBrandName = (brand, lang) => {
  if (typeof brand.name === 'object' && brand.name !== null) {
    return brand.name[lang] || brand.name.en || '';
  }
  return brand.name || '';
};

const AdminBrands = () => {
  const { toast } = useToast();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [formData, setFormData] = useState({ name_en: '', name_ar: '', logo_url: '', order: '0' });

  const loadBrands = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminApi.get('/brands');
      setBrands(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load brands', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { loadBrands(); }, [loadBrands]);

  const resetForm = () => {
    setEditingBrand(null);
    setFormData({ name_en: '', name_ar: '', logo_url: '', order: '0' });
  };

  const handleEdit = (brand) => {
    setEditingBrand(brand);
    const nameEn = typeof brand.name === 'object' ? (brand.name.en || '') : (brand.name || '');
    const nameAr = typeof brand.name === 'object' ? (brand.name.ar || '') : '';
    setFormData({ name_en: nameEn, name_ar: nameAr, logo_url: brand.logo_url || '', order: brand.order?.toString() || '0' });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name_en) {
      toast({ title: 'Error', description: 'English name is required', variant: 'destructive' });
      return;
    }
    try {
      const data = {
        name: { en: formData.name_en, ar: formData.name_ar || formData.name_en },
        logo_url: formData.logo_url,
        is_active: true,
        order: parseInt(formData.order) || 0
      };
      if (editingBrand) {
        await adminApi.put(`/brands/${editingBrand.id}`, data);
        toast({ title: 'Success', description: 'Brand updated' });
      } else {
        await adminApi.post('/brands', data);
        toast({ title: 'Success', description: 'Brand added' });
      }
      setIsDialogOpen(false);
      resetForm();
      loadBrands();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save brand', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this brand?')) return;
    try {
      await adminApi.delete(`/brands/${id}`);
      toast({ title: 'Success', description: 'Brand deleted' });
      loadBrands();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  return (
    <div data-testid="admin-brands-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl sm:text-3xl font-bold text-gray-900">Car Brands</h2>
          <p className="text-gray-600 mt-1">Manage car brands used for product categories</p>
        </div>
        <Button className="bg-red-600 hover:bg-red-700" onClick={() => { resetForm(); setIsDialogOpen(true); }} data-testid="add-brand-btn">
          <Plus className="mr-2 h-4 w-4" />Add Brand
        </Button>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setIsDialogOpen(open); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingBrand ? 'Edit Brand' : 'Add New Brand'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Brand Name (English) *</Label><Input value={formData.name_en} onChange={(e) => setFormData({ ...formData, name_en: e.target.value })} required placeholder="e.g. Toyota" data-testid="brand-name-en-input" /></div>
            <div><Label>Brand Name (Arabic)</Label><Input value={formData.name_ar} onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })} placeholder="e.g. تويوتا" dir="rtl" data-testid="brand-name-ar-input" /></div>
            <ImageUpload label="Brand Logo (Optional)" value={formData.logo_url} onChange={(url) => setFormData({ ...formData, logo_url: url })} />
            <div><Label>Display Order</Label><Input type="number" value={formData.order} onChange={(e) => setFormData({ ...formData, order: e.target.value })} /></div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" className="bg-red-600 hover:bg-red-700" data-testid="save-brand-btn">{editingBrand ? 'Update Brand' : 'Add Brand'}</Button>
              <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div></div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {brands.map((brand) => (
            <div key={brand.id} className="border rounded-lg p-4 text-center hover:shadow-md transition-shadow group" data-testid={`brand-item-${brand.id}`}>
              {brand.logo_url ? (
                <img src={brand.logo_url} alt={getBrandName(brand, 'en')} className="h-16 w-16 object-contain mx-auto mb-2" />
              ) : (
                <div className="h-16 w-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Car className="h-8 w-8 text-gray-400" />
                </div>
              )}
              <h3 className="font-bold text-gray-900 text-sm">{getBrandName(brand, 'en')}</h3>
              {typeof brand.name === 'object' && brand.name.ar && (
                <p className="text-xs text-gray-500" dir="rtl">{brand.name.ar}</p>
              )}
              <div className="flex gap-1 justify-center mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleEdit(brand)}><Edit className="h-3 w-3" /></Button>
                <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => handleDelete(brand.id)}><Trash2 className="h-3 w-3" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminBrands;
